import { Text } from 'designSystem/components/Text'
import { InstrumentTimeSeriesTooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/InstrumentTimeSeriesTooltip'
import { OffPageConnectorTooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/OffPageConnectorTooltip'
import { DocumentViewerAssetType } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import React from 'react'

import { Tooltip, TooltipLabel } from './styles'
import { Box } from './types'

interface MatchedDatapoint {
  id: string
  pi_tag: string
  name: string
  description: string
}

interface PositionedTooltipProps {
  tooltipBox: Box
  tooltipPosition: { x: number; y: number }
  viewportWidth: number
  viewportHeight: number
  hasTimeSeriesData: (label: string | undefined) => boolean
  getMatchedDatapoints: (label: string | undefined) => MatchedDatapoint[]
  offPageConnectorDocuments: SystemDocumentEntry[]
  isLoadingOffPageConnector: boolean
  tooltipLinkedCount: number
}

/**
 * Calculates optimal tooltip position to keep it within viewport bounds.
 */
export function calculateTooltipPosition(
  tooltipPosition: { x: number; y: number },
  estimatedWidth: number,
  estimatedHeight: number,
  viewportWidth: number,
  viewportHeight: number
): { leftPos: number; topPos: number; renderBelow: boolean } {
  let leftPos = tooltipPosition.x
  const halfWidth = estimatedWidth / 2
  const minLeft = halfWidth + 10
  const maxLeft = viewportWidth - halfWidth - 10

  if (leftPos < minLeft) {
    leftPos = minLeft
  } else if (leftPos > maxLeft) {
    leftPos = maxLeft
  }

  const spaceAbove = tooltipPosition.y
  const spaceBelow = viewportHeight - tooltipPosition.y

  // Prefer above, but render below if insufficient space above and sufficient below
  const renderBelow =
    spaceAbove < estimatedHeight && spaceBelow > estimatedHeight

  const topPos = renderBelow
    ? Math.min(tooltipPosition.y + 20, viewportHeight - estimatedHeight - 10)
    : Math.max(tooltipPosition.y - 10, estimatedHeight + 10)

  return { leftPos, topPos, renderBelow }
}

export function PositionedTooltip({
  tooltipBox,
  tooltipPosition,
  viewportWidth,
  viewportHeight,
  hasTimeSeriesData,
  getMatchedDatapoints,
  offPageConnectorDocuments,
  isLoadingOffPageConnector,
  tooltipLinkedCount,
}: PositionedTooltipProps) {
  const isTimeSeriesToolip = hasTimeSeriesData(tooltipBox.label)
  const estimatedHeight = isTimeSeriesToolip ? 350 : 80
  const estimatedWidth = isTimeSeriesToolip ? 320 : 200

  const { leftPos, topPos, renderBelow } = calculateTooltipPosition(
    tooltipPosition,
    estimatedWidth,
    estimatedHeight,
    viewportWidth,
    viewportHeight
  )

  return (
    <Tooltip
      $left={leftPos}
      $top={topPos}
      $transform={renderBelow ? 'translate(-50%, 0)' : 'translate(-50%, -100%)'}
      $noPadding={isTimeSeriesToolip}>
      {tooltipBox.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR ? (
        <OffPageConnectorTooltip
          box={tooltipBox}
          systemDocumentEntries={offPageConnectorDocuments}
          isLoading={isLoadingOffPageConnector}
        />
      ) : isTimeSeriesToolip ? (
        <InstrumentTimeSeriesTooltip
          label={tooltipBox.label || `Box ${tooltipBox.id}`}
          matchedDatapoints={getMatchedDatapoints(tooltipBox.label)}
          linkedDocumentCount={tooltipLinkedCount}
        />
      ) : (
        <>
          <TooltipLabel>
            {tooltipBox.label || `Box ${tooltipBox.id}`}
          </TooltipLabel>
          {tooltipBox.type && (
            <Text as='div' variant='caption' color='muted'>
              Type: {tooltipBox.type}
            </Text>
          )}
          {tooltipLinkedCount > 0 && (
            <Text as='div' variant='caption' color='muted'>
              {tooltipLinkedCount}{' '}
              {tooltipLinkedCount === 1 ? 'document' : 'documents'}
            </Text>
          )}
        </>
      )}
    </Tooltip>
  )
}
